import PuzzleStreakSave from "../learning/PuzzleStreakSave";
import { Button } from "@/components/ui/button";
import React, { useState, useEffect, useCallback, useRef } from "react";
import PageShell from "@/components/layout/PageShell";
import PageHeader from "@/components/layout/PageHeader";
import useTranslation from "next-translate/useTranslation";
import { FaCheck, FaPuzzlePiece } from "react-icons/fa";
import Board from "../../chessBoard/components/Board";
import {
  validateUserMove,
  choosePuzzleAtIndex,
  setHint,
  usePuzzle,
  initPuzzles,
  clearFen,
} from "../../redux/puzzle";
import styles from "./styles/Puzzle.module.css";
import Head from "next/head";

import { useRouter } from "next/router";
import { useSettings } from "@/redux/settings";
import { useMembership } from "@/redux/membership";
import { REGULAR_TIER, UNLIMITED_TIER } from "@chesshotel/constants";
import Script from "next/script";
import { FaSpinner } from "react-icons/fa";
import { preloadDailyPuzzle } from "./dailyPuzzlePrefetch";

export default function Puzzle({ puzzles, nextPuzzleId, index }) {
  const { t } = useTranslation("common");
  const { t: puzzleT } = useTranslation("home");
  const router = useRouter();

  const [boardSize, setBoardSize] = useState<number | null>(null);
  const [loadingNextPuzzle, setLoadingNextPuzzle] = useState(false);

  const settings = useSettings();

  useEffect(() => {}, [puzzles]);

  const {
    fen,
    level,
    playingWhite,
    puzzlesDone,
    list,
    highLightPos,
    possibleMoves,
    animate,
    lastMove,
    id,
    resetKey,
  } = usePuzzle();
  const { tier } = useMembership();

  // Debounced resize handler ref
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateChessboardSize = useCallback(() => {
    if (window.innerWidth >= 768) {
      // Leave room for page gutters, board coordinates, and the difficulty controls.
      const availableWidth = window.innerWidth - 64 - 44 - 32 - 224;
      const size = Math.min(504, availableWidth);
      setBoardSize(size - (size % 4));
    } else {
      let screenWidth = window.innerWidth;
      screenWidth -= screenWidth % 4;
      setBoardSize(screenWidth);
    }
  }, []);

  const debouncedResizeHandler = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(updateChessboardSize, 100);
  }, [updateChessboardSize]);

  useEffect(() => {
    puzzles && puzzles.length && initPuzzles({ puzzles, nextPuzzleId });

    // Initial size update (not debounced)
    updateChessboardSize();

    window.addEventListener("resize", debouncedResizeHandler);
    window.addEventListener("orientationchange", debouncedResizeHandler);

    return () => {
      window.removeEventListener("resize", debouncedResizeHandler);
      window.removeEventListener("orientationchange", debouncedResizeHandler);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      clearFen();
    };
  }, [puzzles, updateChessboardSize, debouncedResizeHandler]);

  useEffect(() => {
    if (!nextPuzzleId) return;

    const nextPuzzleUrl = `/puzzle/${nextPuzzleId}`;
    void preloadDailyPuzzle(nextPuzzleId).catch(() => undefined);
    void router.prefetch(nextPuzzleUrl).catch(() => undefined);
  }, [nextPuzzleId, router]);

  const openNextPuzzle = useCallback(async () => {
    if (!nextPuzzleId || loadingNextPuzzle) return;

    const nextPuzzleUrl = `/puzzle/${nextPuzzleId}`;
    setLoadingNextPuzzle(true);

    try {
      await Promise.all([
        preloadDailyPuzzle(nextPuzzleId),
        router.prefetch(nextPuzzleUrl).catch(() => undefined),
      ]);
      await router.push(nextPuzzleUrl);
    } catch (error) {
      console.error("Unable to preload next puzzle", nextPuzzleId, error);
      setLoadingNextPuzzle(false);
    }
  }, [loadingNextPuzzle, nextPuzzleId, router]);

  const easyDone = puzzlesDone[0] ? styles.doneCheckActive : styles.doneCheck;
  const mediumDone = puzzlesDone[1] ? styles.doneCheckActive : styles.doneCheck;
  const hardDone = puzzlesDone[2] ? styles.doneCheckActive : styles.doneCheck;

  const playingColorText = playingWhite
    ? puzzleT("Find the best move for white")
    : puzzleT("Find the best move for black");

  return (
    <PageShell compactMobile>
      <PageHeader
        title={
          index ? puzzleT("Daily chess puzzles") : `${t("Puzzles")} #${id}`
        }
        description={playingColorText}
        icon={FaPuzzlePiece}
        compactMobile
      />
      <Head>
        <title>{"Chess Hotel | " + puzzleT("Daily chess puzzles")}</title>
        <meta name="description" content={puzzleT("puzzle_description")} />
        <meta name="keywords" content={puzzleT("puzzle_keywords")} />
      </Head>
      {list?.[level] && <PuzzleStreakSave source="daily" puzzleId={list[level].id} completed={!!puzzlesDone[level]} moves={list[level].moves.filter((_, moveIndex) => moveIndex % 2 === 1).map((move) => `${move.from}${move.to}${move.promotion || ""}`)} />}
      <div className="-mx-6 flex flex-col items-center sm:-mx-8 md:mx-0">
        <div className={styles.puzzle}>
          <span id="board">
            {boardSize ? (
              <Board
                key={`${id}-${resetKey}`}
                fen={fen}
                lastMove={lastMove}
                animate={animate}
                size={boardSize}
                playingWhite={playingWhite}
                onMoveMade={validateUserMove}
                highLightKey={highLightPos}
                possibleMoves={possibleMoves}
                settings={settings}
              />
            ) : (
              <div className="w-[100vw] aspect-square md:w-[504px] md:h-[504px]" />
            )}
          </span>
          <div className="flex flex-col items-center md:ml-8 md:items-start">
            <div className="flex mt-4 space-x-2 md:flex-col md:space-x-0 md:mt-0">
              <div className="flex flex-col items-center my-2 md:flex-row">
                <Button
                  variant={level === 0 ? "selected" : "neutral"}
                  aria-pressed={level === 0}
                  className="md:w-[140px]"
                  onClick={() => choosePuzzleAtIndex(0)}
                >
                  {puzzleT("Easy")}
                </Button>
                <div className={styles.doneCont}>
                  <div className={easyDone}>
                    <FaCheck />
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center my-2 md:flex-row">
                <Button
                  variant={level === 1 ? "selected" : "neutral"}
                  aria-pressed={level === 1}
                  className="md:w-[140px]"
                  onClick={() => choosePuzzleAtIndex(1)}
                >
                  {puzzleT("Medium")}
                </Button>
                <div className={styles.doneCont}>
                  <div className={mediumDone}>
                    <FaCheck />
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center my-2 md:flex-row">
                <Button
                  variant={level === 2 ? "selected" : "neutral"}
                  aria-pressed={level === 2}
                  className="md:w-[140px]"
                  onClick={() => choosePuzzleAtIndex(2)}
                >
                  {puzzleT("Hard")}
                </Button>
                <div className={styles.doneCont}>
                  <div className={hardDone}>
                    <FaCheck />
                  </div>
                </div>
              </div>
              <div className="flex flex-col my-2 md:flex-row">
                <Button
                  variant="subtle"
                  className="md:w-[140px]"
                  onClick={() => setHint()}
                >
                  {puzzleT("Hint")}
                </Button>
              </div>
            </div>
            <div className="flex md:m-1 md:mt-14">
              {nextPuzzleId ? (
                <Button
                  onClick={openNextPuzzle}
                  disabled={loadingNextPuzzle}
                  aria-busy={loadingNextPuzzle}
                >
                  {loadingNextPuzzle && (
                    <FaSpinner className="mr-2 animate-spin" aria-hidden />
                  )}
                  {puzzleT("Next puzzle")}
                </Button>
              ) : (
                <Button disabled>{puzzleT("Next puzzle")}</Button>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* {process.env.NODE_ENV === "production" &&
        tier !== REGULAR_TIER &&
        tier !== UNLIMITED_TIER && (
          <Script
            strategy="afterInteractive"
            src="https://app.tinyadz.com/scripts/ads.js?siteId=680177da8c1e965f80fde864"
            type="module"
          />
        )} */}
    </PageShell>
  );
}
